import { documentReady } from "@sazka/utils/document-ready";
import { initializeNavigationAnalytics } from "@/shared/utils/analytics/analytics-secondary-nav";
import { OnlineTicketsCategoryPageAnalytics } from "@/tickets/utils/online-tickets-analytics-category-page";
import { initializeCPWidgetBannerAnalytics } from "@/shared/utils/analytics/analytics-cp-widget-banner";
import { useShowMoreFeature } from "./composables/use-show-more-feature";
import { selectedSorting, sortedFiltersString } from "./components/tickets-filter/tickets-filter-state";
import { useFavoriteTickets } from "./composables/use-favorite-tickets";

type TicketCardSection = {
    containerSelector: string;
    section: OnlineTicketsCategoryPageAnalytics.OnlineTicketsPlacement;
    cardSelector: string;
};

// Selectors for different sections of the ticket cards on category page
const TICKET_CARD_SECTIONS: TicketCardSection[] = [
    {
        containerSelector: "#card-list",
        section: OnlineTicketsCategoryPageAnalytics.OnlineTicketsPlacement.AllScratches,
        cardSelector: ".ds-product-card__link",
    },
    {
        containerSelector: ".cp-favorite-tickets__app",
        section: OnlineTicketsCategoryPageAnalytics.OnlineTicketsPlacement.FavoriteScratches,
        cardSelector: ".ds-product-card__link",
    },
    {
        containerSelector: "#promo-news-slider",
        section: OnlineTicketsCategoryPageAnalytics.OnlineTicketsPlacement.NewScratches,
        cardSelector: ".ds-product-card__link",
    },
    {
        containerSelector: ".cp-most-played-tickets-widget",
        section: OnlineTicketsCategoryPageAnalytics.OnlineTicketsPlacement.MostPlayed,
        cardSelector: ".ds-product-card__link",
    },
    {
        containerSelector: ".cp-ticket-family",
        section: OnlineTicketsCategoryPageAnalytics.OnlineTicketsPlacement.FamilyScratches,
        cardSelector: ".cp-ticket-family__ticket-card-link",
    },
];

// Handler for clicking any of the ticket cards on category page
function attachCardClickListener(
    container: Element,
    section: OnlineTicketsCategoryPageAnalytics.OnlineTicketsPlacement,
    cardSelector: string
): void {
    container.addEventListener("click", (event) => {
        const target = event.target as HTMLElement;
        const card = target.closest(cardSelector);
        if (card) {
            processCardClick(card, container, section, cardSelector);
        }
    });
}

function processCardClick(
    card: Element,
    container: Element,
    section: OnlineTicketsCategoryPageAnalytics.OnlineTicketsPlacement,
    cardSelector: string
): void {
    const gameCode = card.getAttribute("data-game-code");
    const position = getPositionOfVisibleCard(card, container, cardSelector);

    if (!gameCode && position < 0) return;

    if (section === OnlineTicketsCategoryPageAnalytics.OnlineTicketsPlacement.AllScratches) {
        OnlineTicketsCategoryPageAnalytics.pushTicketClick(
            gameCode,
            section,
            position.toString(),
            sortedFiltersString.value,
            selectedSorting.value.toString()
        );
    } else {
        OnlineTicketsCategoryPageAnalytics.pushTicketClick(gameCode, section, position.toString());
    }
}

function getPositionOfVisibleCard(card: Element, container: Element, cardSelector: string): number {
    const visibleCards = Array.from(container.querySelectorAll(cardSelector)).filter(isCardVisible);
    return visibleCards.indexOf(card) + 1;
}

function isCardVisible(card: Element): boolean {
    const outerElement = card.parentElement?.parentElement;
    if (!outerElement) {
        return false;
    }

    const style = window.getComputedStyle(outerElement);
    return style.display !== "none";
}

function setupTicketCardObservers(): void {
    TICKET_CARD_SECTIONS.forEach(({ containerSelector, section, cardSelector }) => {
        const container = document.querySelector(containerSelector);
        if (container) {
            attachCardClickListener(container, section, cardSelector);
        }
    });
}

function setupAnalyticsHandlers(): void {
    // Most played tickets analytics
    const mostPlayedWidgetCards = document.querySelectorAll(".cp-most-played-tickets-widget .ds-product-card__link");

    mostPlayedWidgetCards.forEach((card, index) => {
        const gameCode = card.getAttribute("data-game-code");
        if (gameCode) {
            OnlineTicketsCategoryPageAnalytics.pushActionImpressProducts(
                gameCode,
                OnlineTicketsCategoryPageAnalytics.OnlineTicketsPlacement.MostPlayed,
                index + 1
            );
        }
    });

    // Favorite tickets analytics
    const favoriteWidget = document.querySelector(".authenticated > .cp-favorite-tickets");
    const processedGameCodes = new Set<string>();

    if (favoriteWidget) {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.addedNodes.length) {
                    const favoriteCards = favoriteWidget.querySelectorAll(".ds-product-card__link");
                    favoriteCards.forEach((card: Element, index: number) => {
                        const gameCode = card.getAttribute("data-game-code");
                        if (gameCode && !processedGameCodes.has(gameCode)) {
                            OnlineTicketsCategoryPageAnalytics.pushActionImpressProducts(
                                gameCode,
                                OnlineTicketsCategoryPageAnalytics.OnlineTicketsPlacement.FavoriteScratches,
                                index + 1
                            );
                            processedGameCodes.add(gameCode);
                        }
                    });
                }
            });
        });

        observer.observe(favoriteWidget, {
            childList: true,
            subtree: true,
        });
    }
}

documentReady(() => {
    OnlineTicketsCategoryPageAnalytics.pushLosyLobbyPage();

    setupTicketCardObservers();
    setupAnalyticsHandlers();
    initializeNavigationAnalytics();
    initializeCPWidgetBannerAnalytics();
    useShowMoreFeature().initialize();
    useFavoriteTickets();
});
